/**
 * Media Sidebar Component
 *
 * Provides navigation between different asset sources.
 */

import { useMemo } from 'react';
import { AssetSource, LibrarySource, MediaType } from '../types';
import { MEDIA_TYPE_LABELS } from '../utils';
import type { ProviderRegistry } from '../providers';
import { HomeIcon, BinIcon, TileIcon, FindIcon, PlusIcon, UserIcon, StarsIcon } from '../../../icons';
import { useMode } from '../context/ModeContext';
import { ZprTooltip } from '@zpr-stencil';
import { useAssetActions } from '../context/AssetActionsContext';

export interface MediaSidebarProps {
  /** Provider registry containing all available asset providers */
  registry: ProviderRegistry;
  /** Current media type being browsed (optional - shows all types if not provided) */
  mediaType?: MediaType | null;
  useMediaTypeAstitle?: boolean;
  showWorkspaceAssets: boolean;
  /** Currently active source */
  activeSource: AssetSource;
  /** Indicates if the sidebar is currently in the home view */
  isHomeView?: boolean;
  /** Whether to show category sections */
  showCategories?: boolean;
  /** List of available categories */
  categories?: string[];
  /** Currently selected category filter */
  selectedCategory?: string | null;
  /** Callback when source changes */
  onSourceChange: (source: AssetSource) => void;
  /** Callback when category is selected */
  onCategorySelect?: (category: string | null) => void;
  /** Callback for back navigation */
  onBack?: () => void;
  /** Custom CSS class */
  className?: string;
}

interface SourceOption {
  source: AssetSource;
  label: string;
  icon?: React.ReactNode;
}

// Fixed library sources configuration
const LIBRARY_SOURCES: Array<{ source: LibrarySource; label: string; requiresWorkspace: boolean; icon?: React.ReactNode }> = [
  { source: 'workspace-library', label: 'Workspace assets', requiresWorkspace: true, icon: <TileIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" /> },
  { source: 'user-library', label: 'Personal (legacy)', requiresWorkspace: false, icon: <UserIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" /> },
  { source: 'trash', label: 'Bin', requiresWorkspace: false, icon: <BinIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)"/> },
];

// Default discover sources (external providers will be added dynamically from registry)
const DISCOVER_SOURCES: Array<{ source: AssetSource; label: string; icon?: React.ReactNode; [key: string]: any }> = [
  { source: 'explore', label: 'Explore assets', icon: <FindIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" /> },
  { source: 'request', label: 'Request asset', icon: <PlusIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />, premium: true },
  { source: 'ai-generate', label: 'AI generate', icon: <StarsIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />, disabled: true, tooltip: 'Coming soon' },
];
const NESTLE_SERVICE_NOW_LINK_URL = 'https://nestle.service-now.com/itsp?id=sc_cat_item&sys_id=19b11f8f0f037e10dce16fc800d1b27d&sysparm_domain_restore=false&sysparm_stack=no'

// Zappar library configuration
// const _ZAPPAR_LIBRARY = {
//   source: 'zappar-library' as const,
//   label: 'Zappar library',
//   supportedTypes: [MediaType.Image, MediaType.Video, MediaType.Audio, MediaType.Model3D],
//   icon: <PlusIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />,
// };

export const MediaSidebar = ({
  registry,
  mediaType,
  useMediaTypeAstitle = false,
  showWorkspaceAssets,
  activeSource,
  isHomeView = false,
  showCategories = false,
  categories = [],
  selectedCategory = null,
  onSourceChange,
  onCategorySelect,
  onBack,
  className = '',
}: MediaSidebarProps) => {
  const mediaTypeLabel = (useMediaTypeAstitle && mediaType) ? MEDIA_TYPE_LABELS[mediaType] : 'Media library';
  const { useNestleFeatures } = useMode();
  const { hasPersonalAssets } = useAssetActions();

  // Build source lists dynamically
  const { librarySources, discoverSources } = useMemo(() => {
    const library: SourceOption[] = [];

    // Check if Zapworks provider exists
    const zapworksProvider = registry.get('zapworks');
    // If no media type is selected, show all sources; otherwise check if provider supports the type
    const supportsType = !mediaType || zapworksProvider?.getSupportedTypes().includes(mediaType);

    if (zapworksProvider && supportsType) {
      // Add fixed library sources
      for (const config of LIBRARY_SOURCES) {
        if (!config.requiresWorkspace || showWorkspaceAssets) {
          library.push({ source: config.source, label: config.label, icon: config.icon });
        }
      }


      // // Add Zappar library if media type is supported (or no type filter)
      // if (!mediaType || ZAPPAR_LIBRARY.supportedTypes.includes(mediaType)) {
      //   discover.push({ source: ZAPPAR_LIBRARY.source, label: ZAPPAR_LIBRARY.label, icon: ZAPPAR_LIBRARY.icon });
      // }
    }

    // // Add external providers from registry
    // for (const provider of registry.getAll()) {
    //   const providerId = provider.getId();

    //   // Skip zapworks as it's handled above
    //   if (providerId === 'zapworks') continue;

    //   // Check if provider supports the current media type (or no type filter)
    //   if (!mediaType || provider.getSupportedTypes().includes(mediaType)) {
    //     discover.push({
    //       source: providerId,
    //       label: provider.getDisplayName(),
    //       icon: <PlusIcon width={16} height={16} />,
    //     });
    //   }
    // }

    return { librarySources: library, discoverSources: DISCOVER_SOURCES };
  }, [registry, mediaType, showWorkspaceAssets]);

  const handleSourceClick = (source: AssetSource) => {
    onSourceChange(source);
  };

  return (
    <nav className={`media-sidebar ${className}`}>

      <div className="media-sidebar__title">
        <h2>{mediaTypeLabel}</h2>
      </div>

      <ul className="media-sidebar__main">
        <li className={`media-sidebar__nav-item ${isHomeView ? 'active' : ''}`}>
          <a
            role="button"
            tabIndex={0}
            className={`${isHomeView ? 'active' : ''}`}
            onClick={onBack}
            aria-label="Home – go back to media type selection"
          >
            <HomeIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
            <span className="media-sidebar__nav-item-label">Home</span>
          </a>
        </li>

        {librarySources.length > 0 && (
          <>
            <li role="separator"><hr /></li>
            <li className="media-sidebar__section">
              <h3 className="media-sidebar__section-title">My library</h3>
              <ul className="media-sidebar__nav">
                {librarySources.map(option => {
                  if (option.source === 'user-library' && !hasPersonalAssets) {
                    return null; // Skip personal library if user has no personal assets
                  }
                  return (
                    <li key={option.source} className={`media-sidebar__nav-item ${!isHomeView && activeSource === option.source ? 'active' : ''}`}>
                      <a
                        role="button"
                        tabIndex={0}
                        className={`${
                          !isHomeView && activeSource === option.source ? 'active' : ''
                        }`}
                        onClick={() => handleSourceClick(option.source)}
                        aria-pressed={!isHomeView && activeSource === option.source}
                      >
                        {option?.icon}
                        <span className="media-sidebar__nav-item-label">{option.label}</span>
                      </a>
                    </li>
                  )})}
              </ul>
            </li>
          </>
        )}

        {discoverSources.length > 0 && (
          <><li role="separator"><hr /></li>
          <li className="media-sidebar__section">
            <h3 className="media-sidebar__section-title">Discover</h3>
            <ul className="media-sidebar__nav">
              {discoverSources.map((option) => {
                if (option.premium && !useNestleFeatures) {
                  return null; // Skip premium options if user doesn't have access
                }
                return (
                  <li key={option.source} className={`media-sidebar__nav-item ${activeSource === option.source ? 'active' : ''} ${option.disabled ? 'disabled' : ''}`}>
                    {option.tooltip ? (
                      <ZprTooltip action="hover" placement="top">
                        <a slot="trigger" role="button" tabIndex={-1} aria-disabled="true" className={`  disabled`}>{option.icon}<span className="media-sidebar__nav-item-label">{option.label}</span></a>
                        <p slot="tooltip">{option.tooltip}</p>
                      </ZprTooltip>
                    ) : (
                      <a
                        role="button"
                        tabIndex={option.disabled ? -1 : 0}
                        aria-disabled={option.disabled ? true : undefined}
                        className={`${
                          activeSource === option.source ? 'active' : ''
                        } ${option.disabled ? 'disabled' : ''}`}
                        onClick={() => {
                          if (option.premium) {
                            window.open(NESTLE_SERVICE_NOW_LINK_URL, '_blank');
                            return;
                          }
                          handleSourceClick(option.source)}}
                        aria-pressed={activeSource === option.source}
                      >
                        {option?.icon}
                        <span className="media-sidebar__nav-item-label">{option.label}</span>
                      </a>
                    )}
                  </li>
                )
              })}
            </ul>
          </li>
          </>
        )}

        {showCategories && categories.length > 0 && (
          <><li role="separator"><hr /></li>
          <li className="media-sidebar__section">
            <h3 className="media-sidebar__section-title">Categories</h3>
            <ul className="media-sidebar__nav">
              {categories.map((category) => (
                <li key={category} className={`media-sidebar__nav-item media-sidebar__nav-item--small ${selectedCategory === category ? 'active' : ''}`}>
                  <a
                    role="button"
                    tabIndex={0}
                    onClick={() => onCategorySelect?.(selectedCategory === category ? null : category)}
                    aria-pressed={selectedCategory === category}
                  >
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </a>
                </li>
              ))}
            </ul>
            </li>
          </>
        )}
      </ul>

    </nav>
  );
};